import { EventName } from "module/analytics/types"

import { Button, ButtonGroup, Divider, Heading, SimpleGrid, Text, VStack } from "@chakra-ui/react"
import { Liquidity } from "@perp/sdk-curie"
import { Card } from "component/Card"
import { MotionVStack } from "component/Motion"
import { PerpLink } from "component/PerpLink"
import { Stats } from "component/Stats"
import useDimensions from "react-cool-dimensions"
import { scrollbarStyle } from "theme/scrollbarStyle"

import { useCMSNotes } from "../../hook/useCMSNotes"
import { useLiquidityProviderData } from "../../hook/useLiquidityProviderData"
import { PoolInfo } from "./PoolInfo"

export interface PoolSummaryProps {
    tickerSymbol: string
    liquidityList: Liquidity[]
}

export function PoolSummary({ tickerSymbol, liquidityList }: PoolSummaryProps) {
    const poolInfoCardData = useCMSNotes()
    const { marketBaseAprStr, marketRewardsAprStr, marketTotalAprStr } = useLiquidityProviderData()
    const { observe, width: sidebarWidth } = useDimensions()

    return (
        <Card
            position="relative"
            sx={scrollbarStyle}
            overflowY="auto"
            w="100%"
            h="100%"
            // FIXME: replace this hacky layout fix
            maxH={{ base: "auto", lg: "calc(100vh - 115px)" }}
        >
            <VStack ref={observe} w="full" align="flex-start" spacing={6}>
                {poolInfoCardData && (
                    <MotionVStack
                        my={4}
                        mt={1}
                        textAlign={["center", "left"]}
                        align={["center", "flex-start"]}
                        p={7}
                        spacing={6}
                        borderRadius={"xl"}
                        bgGradient={["linear(to-tr, purple.900, teal.900)"]}
                    >
                        <Heading
                            size="md"
                            bgGradient={[
                                "linear(to-tr, teal.200, yellow.300)",
                                "linear(to-b, blue.100, teal.400)",
                                "linear(to-b, orange.100, purple.300)",
                            ]}
                            bgClip="text"
                        >
                            {poolInfoCardData.title as string}
                        </Heading>
                        <Text fontSize="sm" color="whiteAlpha.700">
                            {poolInfoCardData.description as string}
                        </Text>
                        <ButtonGroup>
                            <PerpLink
                                trackingProps={{ eventName: EventName.LINK_LIQUIDITY_MINING_CLICK }}
                                href={poolInfoCardData.ctaLink as string}
                            >
                                <Button size="sm">{poolInfoCardData.ctaText as string}</Button>
                            </PerpLink>
                        </ButtonGroup>
                    </MotionVStack>
                )}
                <Text fontSize="md" fontWeight="semibold">
                    Pool Info
                </Text>
                <Stats
                    showTooltip={true}
                    size="3xl"
                    label="Total APR"
                    number={`${marketTotalAprStr}`}
                    numberColor={"green.400"}
                />
                <Divider />
                <SimpleGrid w="100%" columns={sidebarWidth < 320 ? 1 : 2} spacing={6}>
                    <Stats
                        showTooltip={true}
                        label="Base APR"
                        number={`${marketBaseAprStr}`}
                        numberColor={"green.400"}
                        size="xl"
                        helper="The est. APR from trading fees"
                    />
                    <Stats
                        showTooltip={true}
                        size="xl"
                        label="Rewards APR"
                        number={`${marketRewardsAprStr}`}
                        numberColor={"green.400"}
                        helper="The est. APR from Pool Party rewards"
                    />
                </SimpleGrid>
                <Divider />
                <SimpleGrid w="100%" columns={sidebarWidth < 320 ? 1 : 2} spacing={6}>
                    <PoolInfo tickerSymbol={tickerSymbol} />
                </SimpleGrid>
            </VStack>
        </Card>
    )
}
